import React, { useState } from 'react';
import ProfileLink from '../common/ProfileLink';
import { Heart, Reply, Trash2 } from 'lucide-react';
import commentService, { EntityComment } from '../../services/commentService';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../i18n';
import AdminBadge from '../common/AdminBadge';
import LinkifiedText from '../common/LinkifiedText';

/**
 * Avatar mit Initialen-Fallback (defensiv gegen kaputte Bild-URLs).
 */
const CommentAvatar: React.FC<{ comment: EntityComment; isReply?: boolean }> = ({
  comment,
  isReply = false,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const avatarUrl = commentService.buildAvatarUrl(comment.authorAvatarUrl, 100);
  const sizeCls = isReply ? 'h-7 w-7 text-[10px]' : 'h-8 w-8 text-[11px]';

  const initials = (() => {
    const name = (comment.authorName || '').trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  })();

  return (
    <div
      className={`${sizeCls} rounded-full bg-zinc-800 border border-zinc-700/60 flex items-center justify-center overflow-hidden shrink-0`}
    >
      {avatarUrl && !imgFailed ? (
        <img
          src={avatarUrl}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="font-semibold text-gray-400 leading-none select-none">
          {initials}
        </span>
      )}
    </div>
  );
};

/**
 * Einzelner Kommentar (Design analog SetupDetailPage-Kommentaren).
 */
const CommentItem: React.FC<{
  comment: EntityComment;
  isReply?: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onReply?: () => void;
}> = ({ comment, isReply = false, canDelete, isDeleting, onDelete, onReply }) => {
  const isDeleted = comment.isDeleted === true;
  const timeLabel = commentService.formatRelativeTime(comment.createdAt);
  const { isAuthenticated } = useAuth();

  // Like-State lokal + optimistisch (2026-08-27); Server-Antwort korrigiert.
  const [liked, setLiked] = useState(comment.likedByMe === true);
  const [likeCount, setLikeCount] = useState(comment.likeCount ?? 0);
  const [liking, setLiking] = useState(false);

  const handleToggleLike = async () => {
    if (!isAuthenticated || liking) return;
    const prevLiked = liked;
    const prevCount = likeCount;
    setLiked(!prevLiked);
    setLikeCount(Math.max(0, prevCount + (prevLiked ? -1 : 1)));
    setLiking(true);
    const result = await commentService.toggleLike(comment.id);
    setLiking(false);
    if (result) {
      setLiked(result.liked);
      setLikeCount(result.likeCount);
    } else {
      setLiked(prevLiked);
      setLikeCount(prevCount);
    }
  };

  return (
    <div
      className={`rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 ${
        isReply ? '' : 'sm:p-4'
      }`}
    >
      <div className="flex items-start gap-3">
        {!isDeleted && comment.authorId ? (
          <ProfileLink userId={comment.authorId} displayName={comment.authorName} className="shrink-0 rounded-full hover:no-underline">
            <CommentAvatar comment={comment} isReply={isReply} />
          </ProfileLink>
        ) : (
          <CommentAvatar comment={comment} isReply={isReply} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {!isDeleted && comment.authorId && comment.authorName && comment.authorName !== 'Unbekannt' ? (
              <ProfileLink
                userId={comment.authorId}
                displayName={comment.authorName}
                className="text-sm font-semibold text-white hover:text-[#3fced1]"
              >
                {comment.authorName}
              </ProfileLink>
            ) : null}
            {!isDeleted && comment.authorId && comment.authorName && comment.authorName !== 'Unbekannt' ? (
              <AdminBadge isAdmin={comment.authorIsAdmin} />
            ) : (
              <span className="text-sm font-semibold text-zinc-500">
                {isDeleted ? t('web.component.comment-item.geloescht') : comment.authorName || t('web.component.comment-item.unbekannt')}
              </span>
            )}
            {timeLabel && (
              <span className="text-[11px] text-zinc-500">{timeLabel}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3">
            {isDeleted ? (
              <p className="min-w-0 flex-1 text-sm text-zinc-500 italic">
                {t('web.component.comment-item.dieser-kommentar-wurde-geloescht')}
              </p>
            ) : (
              <p className="min-w-0 flex-1 text-sm text-zinc-300 whitespace-pre-wrap break-words">
                <LinkifiedText text={comment.content} />
              </p>
            )}
            {!isDeleted && (
              <button
                type="button"
                onClick={handleToggleLike}
                disabled={!isAuthenticated}
                title={isAuthenticated ? (liked ? t('web.component.comment-item.like-entfernen') : t('web.component.comment-item.gefaellt-mir')) : t('web.component.comment-item.zum-liken-anmelden')}
                className={`flex shrink-0 flex-col items-center px-1 transition-colors ${
                  liked ? 'text-[#3fced1]' : 'text-zinc-500 hover:text-[#3fced1]'
                } ${!isAuthenticated ? 'cursor-default opacity-60' : ''}`}
              >
                <Heart className="h-[18px] w-[18px]" fill={liked ? 'currentColor' : 'none'} />
                {likeCount > 0 && (
                  <span className="mt-0.5 text-[11px] font-semibold">{likeCount}</span>
                )}
              </button>
            )}
          </div>
          {!isDeleted && (onReply || canDelete) && (
            <div className="mt-2 flex items-center gap-3">
              {onReply && (
                <button
                  type="button"
                  onClick={onReply}
                  className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#3fced1]"
                >
                  <Reply className="w-3.5 h-3.5" />
                  {t('web.component.comment-item.antworten')}
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-red-400 disabled:opacity-60"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {isDeleting ? t('web.component.comment-item.wird-geloescht') : t('web.component.comment-item.loeschen')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
